/**
 * Portfolio agreement workflow statuses.
 *
 * The API returns these as enum identifiers (`PENDING`, `DONE`, …) and the
 * frontend owns the display labels.
 */

export enum AgreementStatus {
  /** Waiting for the client to approve and submit. Also set when WM returns it for correction. */
  PENDING = "PENDING",
  /** The client submitted; waiting for WM review. */
  PENDING_REVIEW = "PENDING_REVIEW",
  /** The client rejected the document. */
  REJECTED = "REJECTED",
  /** WM approved and final processing completed. */
  DONE = "DONE",
}

export enum AgreementReviewStatus {
  /** Waiting for a WM decision. */
  PENDING_REVIEW = "PENDING_REVIEW",
  /** Backend is generating, storing and distributing the approved PDF. */
  PROCESSING = "PROCESSING",
  /** WM rejected the submission and returned it to the client with feedback. */
  REJECTED = "REJECTED",
  /** WM approved and final processing completed. */
  APPROVED = "APPROVED",
}

/** Visual tone; maps to the chip classes in portfolio-agreement.styles.scss. */
export type StatusTone = "neutral" | "amber" | "blue" | "green" | "red";

export interface StatusView {
  label: string;
  tone: StatusTone;
}

const STATUS_VIEW: Record<AgreementStatus, StatusView> = {
  [AgreementStatus.PENDING]: { label: "Pending client", tone: "amber" },
  [AgreementStatus.PENDING_REVIEW]: { label: "Pending review", tone: "blue" },
  [AgreementStatus.REJECTED]: { label: "Rejected by client", tone: "red" },
  // Kept as "Approved" — the label operators already know this state by.
  [AgreementStatus.DONE]: { label: "Approved", tone: "green" },
};

const REVIEW_VIEW: Record<AgreementReviewStatus, StatusView> = {
  [AgreementReviewStatus.PENDING_REVIEW]: {
    label: "Awaiting review",
    tone: "blue",
  },
  [AgreementReviewStatus.PROCESSING]: { label: "Processing", tone: "amber" },
  [AgreementReviewStatus.REJECTED]: {
    label: "Returned to client",
    tone: "red",
  },
  [AgreementReviewStatus.APPROVED]: { label: "Approved", tone: "green" },
};

/**
 * Uppercases before matching, so the pre-enum values (`"Done"`, `"Pending"`)
 * still resolve while the backend rolls out. Without this, an old `"Done"`
 * would miss every case and fall through to the wrong chip.
 */
const normalise = (value?: string | null): string =>
  (value ?? "").trim().toUpperCase();

/**
 * An unrecognised status shows its own text rather than being forced into one
 * of the known buckets — mislabelling a state is worse than showing a raw
 * value nobody has styled yet.
 */
const fallback = (raw?: string | null): StatusView => ({
  label: (raw ?? "").trim() || "-",
  tone: "neutral",
});

/**
 * Compares a raw API status against an enum member, tolerating the pre-enum
 * casing. Use this instead of `record.status === "Done"` — those literal
 * comparisons are exactly what broke when the API switched to enums.
 */
export const isStatus = (
  value: string | null | undefined,
  target: AgreementStatus
): boolean => normalise(value) === target;

export const statusView = (value?: string | null): StatusView =>
  STATUS_VIEW[normalise(value) as AgreementStatus] ?? fallback(value);

/** `reviewStatus` is null until the client submits. */
export const reviewStatusView = (value?: string | null): StatusView => {
  if (!value) return { label: "Not submitted", tone: "neutral" };
  return REVIEW_VIEW[normalise(value) as AgreementReviewStatus] ?? fallback(value);
};

/** Options for the Status column's filter dropdown, in workflow order. */
export const STATUS_FILTERS = [
  AgreementStatus.PENDING,
  AgreementStatus.PENDING_REVIEW,
  AgreementStatus.REJECTED,
  AgreementStatus.DONE,
].map((value) => ({ text: STATUS_VIEW[value].label, value }));

/** Options for the Review status column's filter dropdown. */
export const REVIEW_STATUS_FILTERS = [
  AgreementReviewStatus.PENDING_REVIEW,
  AgreementReviewStatus.PROCESSING,
  AgreementReviewStatus.REJECTED,
  AgreementReviewStatus.APPROVED,
].map((value) => ({ text: REVIEW_VIEW[value].label, value }));
